import styled, { css } from 'styled-components';
import { BaSeTheme } from '../../../theme';
import { numberWithUnit } from '../../../utils/string-utils';

interface StyledButtonProps {
  className?: string;
  name?: string;
  backgroundColor: string;
  backgroundOnHover: string;
  width: string;
  minWidth: number;
  border: string;
  justifyContent: string;
  boxShadowFocus: string;
  padding: string;
  hoverColor?: string;
  hideBorder?: boolean;
  boxShadow?: boolean;
  thickBorder?: boolean;
  medium?: boolean;
  borderRadius?: number;
}

interface ButtonSize {
  size: string;
}

const buttonWidth = (width: number | string) => css`
  width: ${numberWithUnit(width)};
`;

const getButtonRadius = (radius?: number) => {
  if (!radius && radius !== 0) {
    return '4px';
  }
  return `${radius}px`;
};

const getButtonClassName = ({ className }: StyledButtonProps) => ({
  className: ['BaSe--button', className].filter(Boolean).join(' '),
});

const buttonStyle = css<StyledButtonProps>`
  &.BaSe--button {
    box-sizing: border-box;
    font-family: ${BaSeTheme.typography.fontFamily};
    font-size: ${BaSeTheme.typography.fontSizeBase};
    line-height: ${BaSeTheme.typography.lineHeightBase};
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    position: relative;
    text-align: center;
    display: flex;
    align-items: center;
    transition: ${BaSeTheme.transitions.allPropertiesContinuousFast};
  }

  ${({
    backgroundColor,
    width,
    border,
    padding,
    minWidth,
    justifyContent,
    backgroundOnHover,
    boxShadowFocus,
    hideBorder,
    boxShadow,
    thickBorder,
    medium,
    borderRadius,
  }) => css`
    background-color: ${backgroundColor};
    ${buttonWidth(width)}
    min-width: ${minWidth}px;
    border: ${border};
    padding: ${padding};
    justify-content: ${justifyContent};
    border-radius: ${getButtonRadius(borderRadius)};
    ${medium &&
    css`
      min-height: 40px;
    `}
    ${boxShadow &&
    css`
      box-shadow: ${BaSeTheme.shadows.profundidade2};
    `}

    &:hover {
      cursor: pointer;
      background-color: ${backgroundOnHover};
    }

    &:focus {
      outline: none;
      box-shadow: ${boxShadowFocus};
      ${hideBorder &&
      `border: ${thickBorder ? 2 : 1}px solid ${
        BaSeTheme.colors.defaultColors.transparent
      };`}
    }

    &:active:not(:disabled) {
      box-shadow: none;
      transform: translateY(2px);
    }

    &:disabled {
      cursor: not-allowed;
    }
  `};
`;

export const Button = styled.button.attrs(
  getButtonClassName,
)<StyledButtonProps>`
  ${buttonStyle}
`;

export const AnchorButton = styled.a.attrs(
  getButtonClassName,
)<StyledButtonProps>`
  ${buttonStyle}

  ${({ width }) =>
    width === 'auto' &&
    css`
      width: fit-content;
    `}

  &,
  &:visited,
  &:active,
  &:hover,
  &:focus {
    text-decoration: none;
  }
`;

export const SpinnerContainer = styled.div<ButtonSize>`
  ${({ size }) => css`
    margin-right: ${size === 'big' ? 12 : 4}px;
    display: grid;
    place-items: center;
  `}
`;
